import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { ArrowUpRight, CheckCircle2, WalletCards } from "lucide-react";
import { money, calcCommission, effectiveRate, branchRateOf, calcAgentCommission } from "../lib/finance";
import { PageTitle, Kpi, Empty } from "./common";

const YEAR = new Date().getFullYear();
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export function Commission({ policies, customers, branches, settings }) {
  const [year, setYear] = useState(YEAR);
  const [monthFilter, setMonthFilter] = useState("all"); // "all" | 1..12
  const [branchFilter, setBranchFilter] = useState("Tümü");
  const [customerFilter, setCustomerFilter] = useState("Tümü");

  const yearsAvailable = useMemo(() => {
    const set = new Set(policies.filter((p) => p.start).map((p) => new Date(p.start).getFullYear()));
    set.add(YEAR);
    return Array.from(set).sort((a, b) => b - a);
  }, [policies]);

  const customersAvailable = useMemo(() => {
    const set = new Set(policies.map((p) => (p.customer || "").trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
  }, [policies]);

  const filtered = useMemo(() => policies.filter((p) => {
    if (!p.start) return false;
    const d = new Date(p.start);
    if (d.getFullYear() !== Number(year)) return false;
    if (monthFilter !== "all" && d.getMonth() + 1 !== Number(monthFilter)) return false;
    if (branchFilter !== "Tümü" && p.branch !== branchFilter) return false;
    if (customerFilter !== "Tümü" && (p.customer || "").trim() !== customerFilter) return false;
    return true;
  }), [policies, year, monthFilter, branchFilter, customerFilter]);

  const totals = useMemo(() => {
    let brut = 0, net = 0, agency = 0, personal = 0;
    filtered.forEach((p) => {
      const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
      brut += c.brut; net += c.net; agency += c.agency; personal += c.personal;
    });
    return { brut, net, agency, personal };
  }, [filtered, branches, settings]);

  // Monthly × Branch matrix
  const branchNames = useMemo(() => Array.from(new Set([...branches.map((b) => b.name), ...filtered.map((p) => p.branch)])).filter(Boolean), [branches, filtered]);
  const matrix = useMemo(() => MONTHS.map((_, mIdx) => {
    const row = { monthIndex: mIdx };
    branchNames.forEach((bn) => {
      const rows = filtered.filter((p) => p.branch === bn && new Date(p.start).getMonth() === mIdx);
      row[bn] = rows.reduce((s, p) => s + calcCommission(p.premium, branchRateOf(bn, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
    });
    row.total = branchNames.reduce((s, bn) => s + row[bn], 0);
    row.count = filtered.filter((p) => new Date(p.start).getMonth() === mIdx).length;
    return row;
  }), [filtered, branchNames, branches, settings]);

  const monthlyChart = matrix.map((row, i) => ({ month: MONTHS[i], personal: Number(row.total.toFixed(0)) }));

  return (
    <>
      <PageTitle
        eyebrow="KAZANÇ TAKİBİ"
        title="Prim & Hak Ediş"
        description={`Net = Brüt ÷ ${settings.netDivisor} → Acente = Net × Branş% → Kişisel = Acente × %${settings.personalShare}`}
      />
      <div className="toolbar commission-toolbar">
        <div className="toolbar-actions">
          <label className="filter-inline">
            <span>Yıl</span>
            <select data-testid="commission-year-select" value={year} onChange={(e) => setYear(e.target.value)}>
              {yearsAvailable.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="filter-inline">
            <span>Ay</span>
            <select data-testid="commission-month-select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="all">Tümü</option>
              {MONTHS_FULL.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="filter-inline">
            <span>Branş</span>
            <select data-testid="commission-branch-select" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="Tümü">Tümü</option>
              {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </label>
          <label className="filter-inline">
            <span>Müşteri</span>
            <select data-testid="commission-customer-select" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
              <option value="Tümü">Tümü</option>
              {customersAvailable.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
      </div>
      <div className="kpi-grid">
        <Kpi label="Brüt prim" value={money(totals.brut)} detail={`${year} · toplam üretim`} icon={WalletCards} color="navy" />
        <Kpi label="Net prim" value={money(totals.net)} detail={`Brüt ÷ ${settings.netDivisor}`} icon={ArrowUpRight} color="teal" />
        <Kpi label="Acente komisyonu" value={money(totals.agency)} detail="Net × Branş oranı" icon={CheckCircle2} color="amber" />
        <Kpi label="Kişisel hak ediş" value={money(totals.personal)} detail={`Acente × %${settings.personalShare}`} icon={WalletCards} color="rose" />
      </div>
      <section className="panel report-chart">
        <div className="panel-head"><div><h2>Aylık kişisel hak ediş</h2><p>{year}{monthFilter !== "all" ? ` · ${MONTHS_FULL[Number(monthFilter) - 1]}` : ""} · {branchFilter === "Tümü" ? "tüm branşlar" : branchFilter}{customerFilter !== "Tümü" ? ` · ${customerFilter}` : ""}</p></div></div>
        <div className="chart-wrap tall">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChart}>
              <CartesianGrid vertical={false} stroke="#e8edf3" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}K`} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="personal" name="Kişisel hak ediş" fill="#1a5fe0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section className="panel table-panel matrix-panel">
        <div className="panel-head"><div><h2>Ay × Branş matrisi</h2><p>Kişisel hak ediş dağılımı · {year}</p></div></div>
        <div className="table-scroll">
          <table className="matrix-table">
            <thead>
              <tr>
                <th>AY</th>
                {branchNames.map((b) => <th key={b}>{b.toUpperCase()}</th>)}
                <th>POLİÇE</th>
                <th>TOPLAM</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, i) => (
                <tr key={i} data-testid={`commission-month-${i}`}>
                  <td><strong>{MONTHS_FULL[i]}</strong></td>
                  {branchNames.map((b) => (
                    <td key={b} className={row[b] > 0 ? "has-value" : "muted-cell"}>{row[b] > 0 ? money(row[b]) : "—"}</td>
                  ))}
                  <td>{row.count}</td>
                  <td className="commission"><strong>{money(row.total)}</strong></td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>YILLIK</strong></td>
                {branchNames.map((b) => {
                  const t = matrix.reduce((s, row) => s + row[b], 0);
                  return <td key={b}><strong>{money(t)}</strong></td>;
                })}
                <td><strong>{filtered.length}</strong></td>
                <td className="commission"><strong>{money(totals.personal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel table-panel">
        <div className="panel-head"><div><h2>Poliçe detayı</h2><p>Filtrelenmiş kayıtlar</p></div></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>POLİÇE</th><th>MÜŞTERİ</th><th>BRANŞ</th><th>PLAKA</th><th>BRÜT</th><th>NET</th><th>ŞİRKET</th><th>KİŞİSEL</th><th>ACENTE HAKEDİŞİ</th></tr></thead>
            <tbody>
              {filtered.map((p) => {
                const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
                const ag = calcAgentCommission(p, customers, branches, settings);
                return (
                  <tr key={p.id}>
                    <td><strong>{p.policeNo}</strong><small>{p.start}</small></td>
                    <td>{p.customer}</td>
                    <td><span className="branch-pill">{p.branch}</span></td>
                    <td>{p.plate ? <span className="plate-chip">{p.plate}</span> : <em className="dim">—</em>}</td>
                    <td>{money(c.brut)}</td>
                    <td>{money(c.net)}</td>
                    <td>{p.company || <em className="dim">—</em>}</td>
                    <td className="commission">{money(c.personal)}</td>
                    <td>{ag.agent > 0 ? <span title={`Müşteri komisyonu %${ag.rate}`}>{money(ag.agent)}</span> : <em className="dim">—</em>}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8}><Empty text="Bu filtreye uyan poliçe yok" /></td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------------- Reports (Yıllık Üretim) ----------------
// BackupCcEditor, DeliverySparkline, CronStatusPanel, EmailLogsPanel extracted to /components/
